import { Component, type ReactNode, type ErrorInfo } from "react";
import { captureException } from "@/observability/sentry";
import { logger } from "@/observability/logger";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error("ErrorBoundary caught an error", {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    captureException(error, {
      componentStack: errorInfo.componentStack,
    });
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-6 p-4">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-foreground">
              Something went wrong
            </h1>
            <p className="mt-2 text-muted-foreground">
              An unexpected error occurred. Please try again.
            </p>
          </div>

          {import.meta.env.DEV && this.state.error && (
            <details className="max-w-2xl rounded-lg border border-destructive/50 bg-destructive/10 p-4">
              <summary className="cursor-pointer font-medium text-destructive">
                Error details
              </summary>
              <pre className="mt-2 overflow-auto text-sm text-destructive">
                {this.state.error.message}
                {"\n\n"}
                {this.state.error.stack}
              </pre>
            </details>
          )}

          <div className="flex gap-4">
            <button
              className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md border border-secondary hover:bg-secondary/80"
              onClick={this.handleReset}
            >
              Try again
            </button>
            <button
              className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md border border-destructive hover:bg-destructive/80"
              onClick={this.handleReload}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
